import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

// L4.02 — Workflow definition detail: step + transition graph (read).
// GET /v1/wf/definitions/:id → definition with steps[] (each: conditions, approverRules, tatConfigurations)

interface WfStep {
  readonly stepId: string;
  readonly stepSequence: number;
  readonly stepName: string;
  readonly stepType: string;
  readonly conditions?: { readonly conditionId: string; readonly conditionExpression?: string }[];
  readonly approverRules?: { readonly ruleId: string; readonly resolutionType?: string }[];
  readonly tatConfigurations?: { readonly tatConfigId: string; readonly targetMinutes?: number }[];
}

interface WfDefinitionDetail {
  readonly workflowDefinitionId: string;
  readonly workflowCode: string;
  readonly workflowName: string;
  readonly entityType: string;
  readonly workflowStatus: string;
  readonly isParallelAllowed: boolean;
  readonly maxApprovalLevels: number;
  readonly steps: WfStep[];
}

export function WorkflowDefinitionDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [def, setDef] = useState<WfDefinitionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    apiRequest<WfDefinitionDetail>(`/v1/wf/definitions/${encodeURIComponent(id)}`, { token })
      .then((r) => { if (alive) { setDef(r); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  return (
    <main aria-label="wf-definition-detail">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <Link to="/admin/wf/definitions" data-testid="back-definitions">{t('wf.defDetail.backDefinitions')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('wf.defDetail.title')}</h1>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!def ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="definition-summary" style={{ marginBottom: 20 }}>
            <p data-testid="def-code"><strong>{def.workflowCode}</strong> — {def.workflowName}</p>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              {t('wf.common.entityColon')} {def.entityType} · {t('wf.common.statusColon')} <span data-testid="def-status">{def.workflowStatus}</span>{' '}
              · {t('wf.defDetail.parallelLabel')} {def.isParallelAllowed ? t('wf.common.yesLower') : t('wf.common.noLower')} · {t('wf.defDetail.maxLevels')} {def.maxApprovalLevels}
            </p>
          </section>

          <section aria-label="definition-steps">
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('wf.defDetail.stepsTransitions')}</h2>
            {def.steps.length === 0 ? (
              <p data-testid="steps-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.defDetail.noSteps')}</p>
            ) : (
              <ol aria-label="steps-list" style={{ paddingLeft: 18 }}>
                {def.steps.map((s) => (
                  <li key={s.stepId} data-testid={`step-${s.stepId}`} style={{ marginBottom: 10 }}>
                    <strong>{s.stepSequence}. {s.stepName}</strong> <em>({s.stepType})</em>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                      {t('wf.defDetail.stepCounts', {
                        conditions: s.conditions?.length ?? 0,
                        approverRules: s.approverRules?.length ?? 0,
                        tatRules: s.tatConfigurations?.length ?? 0,
                      })}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </>
      )}
    </main>
  );
}
